"use client";

import { useState, useMemo, useCallback } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isBefore,
  isAfter,
  isWithinInterval,
  startOfDay,
  differenceInCalendarDays,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatINR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export interface DateRange {
  checkIn: Date | null;
  checkOut: Date | null;
}

export interface PricingData {
  [dateKey: string]: number;
}

interface AvailabilityCalendarProps {
  blockedDates?: Date[];
  pricingData?: PricingData;
  onDateRangeSelect: (range: DateRange) => void;
  selectedRange: DateRange;
  minNights?: number;
  basePrice?: number;
}

function isDateBlocked(date: Date, blockedDates: Date[]) {
  return blockedDates.some((bd) => isSameDay(bd, date));
}

function hasBlockedDateInRange(start: Date, end: Date, blockedDates: Date[]) {
  return blockedDates.some(
    (bd) =>
      (isAfter(bd, start) || isSameDay(bd, start)) &&
      (isBefore(bd, end) || isSameDay(bd, end))
  );
}

function MonthGrid({
  month,
  today,
  selectedRange,
  hoveredDate,
  blockedDates,
  pricingData,
  basePrice,
  minNights,
  onDayClick,
  onDayHover,
}: {
  month: Date;
  today: Date;
  selectedRange: DateRange;
  hoveredDate: Date | null;
  blockedDates: Date[];
  pricingData: PricingData;
  basePrice: number;
  minNights: number;
  onDayClick: (day: Date) => void;
  onDayHover: (day: Date | null) => void;
}) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const { checkIn, checkOut } = selectedRange;

  return (
    <div className="flex-1 min-w-0">
      <h3 className="mb-4 text-center font-playfair text-lg font-semibold text-foreground">
        {format(month, "MMMM yyyy")}
      </h3>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="py-1.5 text-center text-xs font-medium text-muted-foreground"
          >
            {wd}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const dateKey = format(day, "yyyy-MM-dd");
          const inMonth = isSameMonth(day, month);
          const isPast = isBefore(day, today);
          const blocked = isDateBlocked(day, blockedDates);
          const disabled = !inMonth || isPast || blocked;

          const isCheckIn = checkIn && isSameDay(day, checkIn);
          const isCheckOut = checkOut && isSameDay(day, checkOut);
          const isSelected = isCheckIn || isCheckOut;

          const inRange =
            checkIn &&
            checkOut &&
            isWithinInterval(day, { start: checkIn, end: checkOut });

          const inHoverRange =
            checkIn &&
            !checkOut &&
            hoveredDate &&
            isAfter(hoveredDate, checkIn) &&
            isWithinInterval(day, { start: checkIn, end: hoveredDate });

          const tooClose =
            checkIn &&
            !checkOut &&
            hoveredDate &&
            differenceInCalendarDays(hoveredDate, checkIn) < minNights &&
            isSameDay(day, hoveredDate);

          const price = pricingData[dateKey] ?? basePrice;

          return (
            <button
              key={dateKey}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onDayClick(day)}
              onMouseEnter={() => !disabled && onDayHover(day)}
              onMouseLeave={() => onDayHover(null)}
              className={cn(
                "relative flex flex-col items-center justify-center rounded-lg py-1.5 text-sm transition-all duration-150",
                "min-h-[3rem] md:min-h-[3.5rem]",
                !inMonth && "invisible",
                disabled && inMonth && "cursor-not-allowed opacity-30",
                !disabled && "cursor-pointer hover:bg-primary/10",
                isSelected && "bg-primary text-primary-foreground z-10",
                !isSelected && inRange && "bg-primary/20 text-foreground",
                !isSelected && inHoverRange && "bg-primary/10 text-foreground",
                tooClose && "ring-1 ring-destructive/50",
                isCheckIn && "rounded-r-none",
                isCheckOut && "rounded-l-none",
                !isSelected && inRange && !isCheckIn && !isCheckOut && "rounded-none",
                !isSelected && inHoverRange && !isCheckIn && "rounded-none"
              )}
            >
              <span className={cn("font-medium", isSelected && "font-bold")}>
                {format(day, "d")}
              </span>
              {!disabled && inMonth && (
                <span
                  className={cn(
                    "text-[10px] leading-none mt-0.5",
                    isSelected
                      ? "text-primary-foreground/80"
                      : "text-muted-foreground"
                  )}
                >
                  {(price / 1000).toFixed(0)}k
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AvailabilityCalendar({
  blockedDates = [],
  pricingData = {},
  onDateRangeSelect,
  selectedRange,
  minNights = 1,
  basePrice = 30000,
}: AvailabilityCalendarProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(today));
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const nextMonth = addMonths(currentMonth, 1);
  const canGoPrev = isAfter(currentMonth, startOfMonth(today));

  const handleDayClick = useCallback(
    (day: Date) => {
      const { checkIn, checkOut } = selectedRange;

      if (!checkIn || checkOut) {
        onDateRangeSelect({ checkIn: day, checkOut: null });
        return;
      }

      if (isBefore(day, checkIn) || isSameDay(day, checkIn)) {
        onDateRangeSelect({ checkIn: day, checkOut: null });
        return;
      }

      if (differenceInCalendarDays(day, checkIn) < minNights) {
        return;
      }

      if (hasBlockedDateInRange(checkIn, day, blockedDates)) {
        onDateRangeSelect({ checkIn: day, checkOut: null });
        return;
      }

      onDateRangeSelect({ checkIn, checkOut: day });
    },
    [selectedRange, onDateRangeSelect, minNights, blockedDates]
  );

  const sharedProps = {
    today,
    selectedRange,
    hoveredDate,
    blockedDates,
    pricingData,
    basePrice,
    minNights,
    onDayClick: handleDayClick,
    onDayHover: setHoveredDate,
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          disabled={!canGoPrev}
          className="text-foreground hover:text-primary"
        >
          <ChevronLeft className="size-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="text-foreground hover:text-primary"
        >
          <ChevronRight className="size-5" />
        </Button>
      </div>

      <AnimatePresence mode="popLayout">
        <motion.div
          key={currentMonth.toISOString()}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col gap-8 md:flex-row md:gap-6"
        >
          <MonthGrid month={currentMonth} {...sharedProps} />
          <MonthGrid month={nextMonth} {...sharedProps} />
        </motion.div>
      </AnimatePresence>

      {selectedRange.checkIn && !selectedRange.checkOut && (
        <p className="mt-4 text-center text-sm text-muted-foreground">
          {minNights > 1
            ? `Select check-out date (min ${minNights} nights)`
            : "Select your check-out date"}
        </p>
      )}

      {selectedRange.checkIn && selectedRange.checkOut && (
        <div className="mt-4 flex items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-sm bg-primary" />
            <span className="text-muted-foreground">
              {differenceInCalendarDays(
                selectedRange.checkOut,
                selectedRange.checkIn
              )}{" "}
              nights selected
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              onDateRangeSelect({ checkIn: null, checkOut: null })
            }
            className="text-primary hover:underline"
          >
            Clear dates
          </button>
        </div>
      )}
    </div>
  );
}
